import type { DraftPayload } from "./draft-buffer";

/**
 * Reading a stored draft back into a mask. The payload comes from the server
 * and may predate a change of the mask, so every field is read defensively:
 * a missing or mistyped value falls back to the mask's own default.
 */

export function readString(payload: DraftPayload, key: string, fallback: string): string {
  const value = payload[key];
  return typeof value === "string" ? value : fallback;
}

export function readStrings(payload: DraftPayload, key: string): string[] {
  const value = payload[key];
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
}

/** A form value as the draft store takes it: strings stay, lists keep their strings, anything else is empty. */
export function toDraftValue(value: unknown): string | string[] {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.filter((entry): entry is string => typeof entry === "string");
  return "";
}

/** The draft store's form of a mask's values, field by field. */
export function toDraftPayload(values: Record<string, unknown>, fields: readonly string[]): DraftPayload {
  const payload: DraftPayload = {};
  for (const field of fields) payload[field] = toDraftValue(values[field]);
  return payload;
}

/** Same content, ignoring key order - "has the mask changed since it started". */
export function samePayload(a: DraftPayload, b: DraftPayload): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (JSON.stringify(a[key] ?? null) !== JSON.stringify(b[key] ?? null)) return false;
  }
  return true;
}
